import { useGT } from 'gt-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { overrideWindowLabel } from '../components/overrideRows';
import { formatFactorPercent } from '../components/forecastFactorFormat';
import type { FactorPeriod } from './forecastFactorSchedule';

export function factorPeriodLabel(
  period: FactorPeriod,
  gt: ReturnType<typeof useGT>,
): string {
  const start = new Date(period.startsAt);
  const last = period.endsAt ? new Date(Date.parse(period.endsAt) - 1) : null;
  if (
    last &&
    start.getUTCFullYear() === last.getUTCFullYear() &&
    start.getUTCMonth() === last.getUTCMonth()
  ) {
    return start.toLocaleDateString(undefined, {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    });
  }
  return overrideWindowLabel({ ...period, reviewAt: null }, gt, 'month');
}

export function ForecastFactorTimeline({
  periods,
}: {
  periods: readonly FactorPeriod[];
}) {
  const gt = useGT();
  return (
    <section className="mb-3 flex flex-col gap-1 text-xs">
      <h5 className="font-medium">
        {gt('Adjustments by forecast month', {
          context: 'growth factor schedule heading',
        })}
      </h5>
      <p className="text-muted-foreground">
        {gt(
          'Saved schedule from this month onward, including dates beyond the current forecast. The newest override wins each month; factors do not add or multiply together.',
          { context: 'growth factor schedule explanation' },
        )}
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              {gt('Forecast months', {
                context: 'growth factor schedule column',
              })}
            </TableHead>
            <TableHead className="text-right">
              {gt('Growth adjustment', {
                context: 'growth factor schedule column',
              })}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {periods.map((period) => (
            <TableRow key={period.startsAt}>
              <TableCell>{factorPeriodLabel(period, gt)}</TableCell>
              <TableCell className="text-right tabular-nums">
                {period.override
                  ? gt('{pct} (x{factor})', {
                      context: 'growth factor schedule multiplier',
                      pct: formatFactorPercent(period.override.factor),
                      factor: String(period.override.factor),
                    })
                  : gt('No growth adjustment (x1)', {
                      context: 'growth factor schedule without an override',
                    })}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <p className="text-muted-foreground">
        {gt(
          'An absolute forecast override takes precedence over the growth adjustment for its month.',
          { context: 'growth factor schedule absolute override precedence' },
        )}
      </p>
    </section>
  );
}
